import React from 'react';

import { range } from 'lodash';
import pdfjs from 'pdfjs-dist';

import { CtaButton, Modal } from '@opendoor/bricks';
import Divider from './Divider';
import PdfSplittingModalRow from './PdfSplittingModalRow';

import './PdfSplittingModalStyle.less';

/*
 * When using Webpack, we have to load the worker manually.
 * Otherwise it tries to request some bogus path like /assets/1.js or something,
 * which we're obviously not handling, and it throws a mime-type error.
 * See: https://github.com/wojtekmaj/react-pdf/blob/master/src/pdf.worker.entry.js
 *
 * Have to check if `window` is undefined, in case we're running on Node.
 */
if (typeof window !== 'undefined') {
  /* tslint:disable:no-var-requires */
  (window as any).pdfjsWorker = require('pdfjs-dist/build/pdf.worker.js');
}

export interface ISplitDocumentMetadata {
  displayName: string;
  endPage: number;
  startPage: number;
  uniqueId?: string;
}

export interface ISplitPdf {
  documentSplittings: ISplitDocumentMetadata[];
  pdfFile: File;
}

export interface IDocumentMetadata {
  displayName: string;
  endPage?: number;
  isNameInvalid?: boolean;
  isPageDataInvalid?: boolean;
  s3Key?: string;
  startPage?: number;
  uniqueId?: string;
}

export interface IPdfSplittingModalProps {
  isOpen: boolean;
  requiredDocuments: IDocumentMetadata[];
  onCloseModal?: () => void;
  onOpenModal?: () => void;
  pdfFile: File;
  pdfSplittingModalDocuments: IDocumentMetadata[];
  submitDocumentToSplit: (splitPdf: ISplitPdf) => void;
}

interface IPdfSplittingModalState {
  documents: IDocumentMetadata[];
  errorMessage: string;
  numPages: number | null;
  pdfLoadError: boolean;
  pageImageData?: string[] | null;
}

export const OVERLAPPING_PAGE_RANGES_MESSAGE = 'Pages for different documents may not overlap';
export const INVALID_PAGE_RANGE_MESSAGE = 'Pages must exist in document';
export const DUPLICATED_DOCUMENT_NAMES_MESSAGE = 'Duplicated document names are not allowed';

export default class PdfSplittingModal extends React.PureComponent<
  IPdfSplittingModalProps,
  IPdfSplittingModalState
> {
  public static getStateForProps = (props: IPdfSplittingModalProps) => ({
    documents:
      props.pdfSplittingModalDocuments.length > 0
        ? props.pdfSplittingModalDocuments
        : props.requiredDocuments,
    errorMessage: '',
    numPages: null,
    pageImageData: null,
    pdfLoadError: false,
  });

  public state = PdfSplittingModal.getStateForProps(this.props);

  public componentDidMount() {
    const { isOpen, onOpenModal, pdfSplittingModalDocuments } = this.props;

    if (isOpen) {
      onOpenModal && onOpenModal();
    }

    if (pdfSplittingModalDocuments.length > 0) {
      this.setState({ documents: pdfSplittingModalDocuments });
    }
  }

  // renders a page of the pdf to a base64 png
  public renderPdfPage(pdf: any, page: number): Promise<string> {
    let canvas;

    return pdf
      .getPage(page)
      .then(page => {
        const viewport = page.getViewport(1);

        // create in-memory canvas
        canvas = document.createElement('canvas');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // render page to in-memory canvas
        return page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
        }).promise;
      })
      .then(() => {
        // rendering completed, grab the data URL
        return canvas.toDataURL('image/png');
      });
  }

  // takes a File and returns its contents for pdfjs to process
  public readFileContents(file: File): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(new Uint8Array(reader.result));
      reader.onerror = reject;

      reader.readAsArrayBuffer(file);
    });
  }

  public componentWillReceiveProps(nextProps: IPdfSplittingModalProps) {
    const {
      isOpen: nextIsOpen,
      pdfFile: nextPdfFile,
      pdfSplittingModalDocuments: nextPdfSplittingModalDocuments,
      requiredDocuments: nextRequiredDocuments,
    } = nextProps;
    const {
      isOpen,
      onOpenModal,
      pdfFile,
      pdfSplittingModalDocuments,
      requiredDocuments,
    } = this.props;

    if (pdfFile !== nextPdfFile && nextPdfFile != null) {
      this.readFileContents(nextPdfFile)
        .then(data => {
          // disable the worker because enabling it causes it to fetch
          // pdf.worker.js, which throws some security error about mime types
          pdfjs.disableWorker = true;

          // read the document
          return pdfjs.getDocument({ data });
        })
        .then(pdf => {
          this.handlePdfFileLoad(pdf);
          const promises = range(1, pdf.numPages + 1).map(i => this.renderPdfPage(pdf, i));
          Promise.all(promises).then((pageImageData: string[]) => {
            this.setState({ pageImageData });
          });
        })
        .catch(() => {
          this.handlePdfFileLoadError();
        });
    }

    if (!isOpen && nextIsOpen) {
      onOpenModal && onOpenModal();
    }

    if (
      pdfFile === nextPdfFile &&
      pdfSplittingModalDocuments === nextPdfSplittingModalDocuments &&
      requiredDocuments === nextRequiredDocuments
    ) {
      return;
    }
    this.setState(PdfSplittingModal.getStateForProps(nextProps));
  }

  public addNewDocument = () => {
    const { documents } = this.state;
    const newDoc: IDocumentMetadata = {
      displayName: '',
      isNameInvalid: false,
      isPageDataInvalid: false,
    };

    this.setState({ documents: [...documents, newDoc] });
  };

  public findDocumentsWithDuplicatedNames = () => {
    const { documents } = this.state;
    const documentNames = {};
    const documentsWithDuplicatedNames = new Set();
    const namedDocuments = documents.filter(
      document => document.displayName && document.displayName.length > 0,
    );
    namedDocuments.forEach(document => {
      const documentName = document.displayName
        .toLowerCase()
        .split(/\s+/)
        .join('');
      const index = documents.indexOf(document);
      if (documentNames[documentName] === undefined) {
        documentNames[documentName] = index;
      } else {
        documentsWithDuplicatedNames.add(documentNames[documentName]);
        documentsWithDuplicatedNames.add(index);
      }
    });
    return documentsWithDuplicatedNames;
  };

  public findDocumentsWithInvalidPageRanges = () => {
    const { documents } = this.state;
    let { numPages } = this.state;

    if (numPages === null) {
      numPages = Infinity;
    }
    const documentsWithRanges = documents.filter(document => {
      return typeof document.startPage === 'number' && typeof document.endPage === 'number';
    });
    const documentsWithInvalidPageRanges = new Set();
    documentsWithRanges.forEach(document => {
      const index = documents.indexOf(document);
      if (
        document.startPage > document.endPage ||
        document.endPage > numPages ||
        document.startPage < 1
      ) {
        documentsWithInvalidPageRanges.add(index);
      }
    });
    return documentsWithInvalidPageRanges;
  };

  public findDocumentsWithOverlappingPageRanges = () => {
    const { documents } = this.state;
    const documentsWithRanges = documents.filter(document => {
      return typeof document.startPage === 'number' && typeof document.endPage === 'number';
    });
    const documentsSortedByStartPage = documentsWithRanges.sort((previous, current) => {
      const previousStartPage = previous.startPage;
      const currentStartPage = current.startPage;

      if (previousStartPage < currentStartPage) {
        return -1;
      }

      if (previousStartPage === currentStartPage) {
        return 0;
      }

      return 1;
    });

    const overlappingPageRanges = documentsSortedByStartPage.reduce(
      (overlappingPageRanges, current, index, arr) => {
        if (index === 0) {
          return overlappingPageRanges;
        }
        const previous = arr[index - 1];

        const previousEnd = previous.endPage;
        const currentStart = current.startPage;
        const overlap = previousEnd >= currentStart;
        const currentIndex = documents.indexOf(current);
        const previousIndex = documents.indexOf(previous);

        if (overlap) {
          overlappingPageRanges.overlap = true;

          overlappingPageRanges.ranges.add(currentIndex);
          overlappingPageRanges.ranges.add(previousIndex);
        }

        return overlappingPageRanges;
      },
      { overlap: false, ranges: new Set() },
    );

    return overlappingPageRanges.ranges;
  };

  public handleSplitDocument = () => {
    const { pdfFile, submitDocumentToSplit } = this.props;
    const { documents } = this.state;
    const anyInvalidData = documents.some(
      document => document.isNameInvalid || document.isPageDataInvalid,
    );
    if (anyInvalidData) {
      return;
    }

    const documentSplittings = documents
      .filter(
        document =>
          typeof document.startPage === 'number' &&
          typeof document.endPage === 'number' &&
          document.displayName.trim(),
      )
      .map(document => ({
        displayName: document.displayName,
        endPage: document.endPage,
        startPage: document.startPage,
        uniqueId: document.uniqueId,
      }));

    submitDocumentToSplit({
      documentSplittings,
      pdfFile,
    });
  };

  public markInvalidInputs = (
    docsWithInvalidPageRanges: Set<number>,
    docsWithInvalidNames: Set<number>,
  ) => {
    const { documents } = this.state;
    const updatedDocuments = documents.map((document, index) => {
      return {
        ...document,
        isNameInvalid: docsWithInvalidNames.has(index),
        isPageDataInvalid: docsWithInvalidPageRanges.has(index),
      };
    });
    this.setState({ documents: updatedDocuments });
  };

  public removeDocument = (index: number) => {
    const { documents } = this.state;
    const updatedDocuments = [...documents.slice(0, index), ...documents.slice(index + 1)];

    this.setState({ documents: updatedDocuments }, this.validateInputs);
  };

  public renameDocument = (index: number, displayName: string) => {
    const { documents } = this.state;
    const oldDoc = documents[index];
    const renamedDoc = {
      ...oldDoc,
      displayName,
    };
    const updatedDocuments = documents.map((document, i) => {
      return i === index ? renamedDoc : document;
    });

    this.setState({ documents: updatedDocuments }, this.validateInputs);
  };

  public updateEndPage = (index: number, endPage: number | undefined) => {
    const { documents } = this.state;
    const outdatedDocument = documents[index];
    const updatedDocument = {
      ...outdatedDocument,
      endPage,
    };
    const updatedDocuments = documents.map(
      (document, i) => (i === index ? updatedDocument : document),
    );

    this.setState({ documents: updatedDocuments }, this.validateInputs);
  };

  public updateStartPage = (index: number, startPage: number) => {
    const { documents } = this.state;
    const outdatedDocument = documents[index];
    const updatedDocument = {
      ...outdatedDocument,
      startPage,
    };
    const updatedDocuments = documents.map(
      (document, i) => (i === index ? updatedDocument : document),
    );

    this.setState({ documents: updatedDocuments }, this.validateInputs);
  };

  public validateInputs = () => {
    const docsWithInvalidPageRanges = this.findDocumentsWithInvalidPageRanges();
    const docsWithOverlappingPageRanges = this.findDocumentsWithOverlappingPageRanges();
    const docsWithDuplicatedNames = this.findDocumentsWithDuplicatedNames();

    const docsWithInvalidPageData: Set<number> = new Set([
      ...docsWithInvalidPageRanges,
      ...docsWithOverlappingPageRanges,
    ]);

    this.markInvalidInputs(docsWithInvalidPageData, docsWithDuplicatedNames);

    if (docsWithInvalidPageRanges.size > 0) {
      this.setState({ errorMessage: INVALID_PAGE_RANGE_MESSAGE });
      return;
    }

    if (docsWithOverlappingPageRanges.size > 0) {
      this.setState({ errorMessage: OVERLAPPING_PAGE_RANGES_MESSAGE });
      return;
    }

    if (docsWithDuplicatedNames.size > 0) {
      this.setState({ errorMessage: DUPLICATED_DOCUMENT_NAMES_MESSAGE });
      return;
    }

    this.setState({ errorMessage: '' });
  };

  public handlePdfFileLoad = (pdfLoadPayload: { numPages: number }) =>
    this.setState({ numPages: pdfLoadPayload.numPages }, this.validateInputs);

  public handlePdfFileLoadError = () => this.setState({ pdfLoadError: true });

  public renderSplittingUI = () => {
    const { requiredDocuments } = this.props;
    const { documents, errorMessage, pageImageData } = this.state;

    return (
      <div>
        <div className="instruction-text">
          Select which pages of your uploaded PDF to apply to which document type (if any). You can
          still upload individual PDFs or split another PDF once you have completed this document.
        </div>
        <br />
        <div className="row document-table-headers">
          <div className="col-md-6 col-md-offset-1">
            <p className="text-small">Document type</p>
          </div>
          <div className="col-md-offset-2">
            <p className="text-small">Pages</p>
          </div>
        </div>
        <Divider />
        {documents.map((document, index) => {
          return (
            <div key={index}>
              <PdfSplittingModalRow
                document={document}
                index={index}
                onRemoveDocument={this.removeDocument}
                onRenameDocument={this.renameDocument}
                onUpdateEndPage={this.updateEndPage}
                onUpdateStartPage={this.updateStartPage}
                pageImageData={pageImageData}
                required={index < requiredDocuments.length}
              />
            </div>
          );
        })}
        <div className="add-additional-document" onClick={this.addNewDocument}>
          <div className="row col-md-8 col-md-offset-1">
            <p className="add-additional-document-text text-small">+ Add additional document</p>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-md-9 error-message">
            <p>{errorMessage}</p>
          </div>
          <div className="col-md-3">
            <CtaButton
              actionText="Split Document"
              actionTextSmall="Split Document"
              onClick={this.handleSplitDocument}
              size="medium"
            />
          </div>
        </div>
      </div>
    );
  };

  public renderPdfLoadError = () => (
    <div className="pdf-load-error-message">
      Sorry, we were not able to read your file.
      <br />
      Please ensure the file is a PDF and try again.
    </div>
  );

  public render(): JSX.Element {
    const { isOpen, onCloseModal } = this.props;
    const { pdfLoadError } = this.state;

    return (
      <Modal
        className="pdf-splitting-modal"
        isOpen={isOpen}
        maxWidth={600}
        onRequestClose={onCloseModal}
      >
        <div className="modal-header">
          <h2>
            <strong>Split your PDF</strong>
          </h2>
        </div>
        {pdfLoadError ? this.renderPdfLoadError() : this.renderSplittingUI()}
      </Modal>
    );
  }
}
